import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { postV1MessagingChannelsIdMute } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MessagingChannel } from '~hooks/useMessaging/models';
import { reReadUnreadCountsSoon } from '~hooks/useMessaging/reReadUnreadCountsSoon';
import useMessagingChannelCachePatch from '~hooks/useMessaging/useMessagingChannelCachePatch';
import connection from '~services/connectionModule';

const channelIdsBeingMutedByClient = new WeakMap<QueryClient, Set<string>>();

function channelIdsBeingMuted(queryClient: QueryClient) {
  const existing = channelIdsBeingMutedByClient.get(queryClient);
  if (existing) return existing;

  const channelIds = new Set<string>();
  channelIdsBeingMutedByClient.set(queryClient, channelIds);

  return channelIds;
}

export default function useMuteChannel() {
  const queryClient = useQueryClient();
  const { patchChannelInCaches } = useMessagingChannelCachePatch();

  const toggleChannelMuted = useCallback(
    async (channel: MessagingChannel) => {
      const beingMuted = channelIdsBeingMuted(queryClient);
      if (beingMuted.has(channel.id)) return;

      const muted = !channel.muted;
      beingMuted.add(channel.id);

      try {
        await postV1MessagingChannelsIdMute({
          path: { id: channel.id },
          body: { muted },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          muted
            ? t('error_messages.messaging.failed_to_mute_channel')
            : t('error_messages.messaging.failed_to_unmute_channel'),
        );
        throw error;
      } finally {
        beingMuted.delete(channel.id);
      }

      patchChannelInCaches(channel.id, (cachedChannel) =>
        cachedChannel.withReadState({ muted }),
      );
      reReadUnreadCountsSoon(queryClient);
    },
    [patchChannelInCaches, queryClient],
  );

  return { toggleChannelMuted };
}
